const _excluded = ["as", "className", "items", "expandedContent", "rowId", "addPulse", "onClick", "size", "actionColumn", "disableShadow", "disableMarginBottom", "intent"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useContext, useMemo, useState } from 'react';
import cn from 'classnames';
import { FOCUS_DISABLED_CLASS } from "../../focus-management";
import { WideArrowUpward, WideArrowForward } from "../../icons";
import { Checkbox } from "../Checkbox";
import { useIntentBehavior } from "../IntentBehavior";
import { TableListItem } from "./TableListItem";
import { actionColumnDef, expandIconStyle, useTableListContext } from "./TableList.utils";
import styles from "./TableList.module.scss";
let idCounter = 0;

const getID = () => {
  const id = (idCounter + 1) % (Number.MAX_SAFE_INTEGER - 1);
  idCounter = id;
  return id;
};

const TableListRowContext = /*#__PURE__*/React.createContext({
  setForceHover() {},

  size: 'medium'
});
export const useTableListRowContext = () => useContext(TableListRowContext);
const TableListRow = /*#__PURE__*/React.forwardRef(function TableListRow(props, ref) {
  const {
    as,
    className,
    items,
    expandedContent,
    rowId,
    addPulse,
    onClick: _onClick,
    size = 'medium',
    actionColumn,
    disableShadow,
    disableMarginBottom,
    intent
  } = props,
        rest = _objectWithoutProperties(props, _excluded);

  const {
    columnDefs,
    expandableRows,
    expandedRows,
    enableRowSelection,
    selectedRows,
    variant = 'default',
    hasActionColumn,
    setSelectedRows,
    toggleExpandedRow
  } = useTableListContext();
  const {
    getDisabled
  } = useIntentBehavior();
  const disabledFromIntent = intent ? getDisabled(intent) : false;
  const [forceHover, setForceHover] = useState(false);
  const id = useMemo(() => rowId !== undefined ? rowId : getID(), [rowId]);
  const isExpanded = expandedRows[id];
  const isSelected = selectedRows[id] || false;
  const RowElement = as || 'div';
  const contextValue = useMemo(() => ({
    setForceHover,
    size
  }), [size]);
  return /*#__PURE__*/React.createElement(TableListRowContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.row, styles["".concat(variant, "RowVariant")], styles["".concat(size, "RowSize")], {
      [styles.hover]: isExpanded || forceHover,
      [styles.selected]: isSelected,
      [styles.pulse]: addPulse,
      [styles.disableShadow]: disableShadow,
      [styles.disableMarginBottom]: disableMarginBottom
    })
  }, /*#__PURE__*/React.createElement(RowElement, _extends({}, rest, {
    ref: ref,
    "data-id": id,
    "data-checked": isSelected,
    disabled: disabledFromIntent,
    className: cn(styles.rowInner, styles["".concat(variant, "RowInnerVariant")], styles["".concat(size, "RowSize")], className, {
      [styles.clickable]: _onClick
    }),
    onClick: event => {
      expandableRows && toggleExpandedRow(id);
      _onClick && _onClick(event);
    }
  }), !!enableRowSelection && /*#__PURE__*/React.createElement("div", {
    className: styles.checkboxWrapper,
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement(Checkbox, {
    "data-testid": props['data-testid'] && "".concat(props['data-testid'], "-checkbox"),
    checked: isSelected,
    onChange: e => {
      setSelectedRows({
        [id]: e.target.checked
      }); // This prevents TableListActions to be visible if we click and select a row.

      if (document.body.classList.contains(FOCUS_DISABLED_CLASS)) {
        e.target.blur();
      }
    }
  })), items.map((item, idx) => /*#__PURE__*/React.createElement(TableListItem, {
    key: idx,
    columnDef: columnDefs[idx],
    item: item
  })), !!hasActionColumn && /*#__PURE__*/React.createElement(TableListItem, {
    columnDef: actionColumnDef,
    item: {
      value: actionColumn || ''
    }
  }), expandableRows && /*#__PURE__*/React.createElement("div", {
    style: expandIconStyle
  }, isExpanded ? /*#__PURE__*/React.createElement(WideArrowUpward, null) : /*#__PURE__*/React.createElement(WideArrowForward, null))), isExpanded && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles.separator
  }), expandedContent && expandedContent())));
});
TableListRow.displayName = 'TableListRow';
export { TableListRow };